import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Send, Loader2 } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { submitLead } from "@/lib/leads.functions";
import { buildLeadAttribution } from "@/lib/lead-attribution";

const serviceOptions = [
  "MOT",
  "Service",
  "Diagnostics",
  "Repair",
  "Bodywork",
  "Other",
];

const urgencyOptions = [
  "Today / ASAP",
  "This week",
  "Next week",
  "Flexible",
];

interface QuoteFormProps {
  preselectedService?: string;
}

export function QuoteForm({ preselectedService }: QuoteFormProps) {
  const biz = getBusinessConfig();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [service, setService] = useState(preselectedService || "");
  const [urgency, setUrgency] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [make, setMake] = useState("");
  const [reg, setReg] = useState("");
  const [message, setMessage] = useState("");
  const [callback, setCallback] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);

  if (submitted) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Send className="h-6 w-6 text-primary" />
        </div>
        <h3 className="mt-4 text-xl font-semibold text-foreground">Thanks — we've got it</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Woodseer Autos will call you back during workshop hours. For urgent bookings, call us — it's the fastest way.
        </p>
        <a
          href={biz.phoneHref}
          onClick={() => trackEvent("call_click", { location: "quote_confirmation" })}
          className="mt-4 inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition-colors"
        >
          <Phone className="h-4 w-4" />
          {biz.phone}
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        trackEvent("contact_submit");

        try {
          const attribution = buildLeadAttribution({
            formLocation: "quote_form",
            ctaLabel: "send_enquiry",
            pageType: "contact",
          });
          await submitLead({
            data: {
              type: "contact" as const,
              service: service || undefined,
              name: name.trim(),
              phone: phone.trim(),
              vehicle_make_model: make.trim() || undefined,
              vehicle_reg: reg.trim() || undefined,
              message: message.trim() || undefined,
              callback_time: callback.trim() || undefined,
              urgency: urgency || undefined,
              sms_opt_in: smsOptIn,
              ...attribution,
            },
          });
          setSubmitted(true);
        } catch (err) {
          setError("Something went wrong. Please try calling us instead.");
        } finally {
          setSubmitting(false);
        }
      }}
      className="rounded-xl border border-border bg-card p-6 md:p-8 space-y-5 shadow-sm"
    >
      <div>
        <h3 className="text-xl font-semibold text-foreground">Send Us a Message</h3>
        <p className="mt-1 text-sm text-muted-foreground">Tell us what you need — we'll get back to you quickly.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input id="name" required minLength={2} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input id="phone" required type="tel" minLength={7} placeholder="Your phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <p className="text-[10px] text-muted-foreground">We'll call you back — usually within a few hours.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="service">What do you need? *</Label>
          <Select value={service} onValueChange={setService}>
            <SelectTrigger>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {serviceOptions.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="urgency">How urgent?</Label>
          <Select value={urgency} onValueChange={setUrgency}>
            <SelectTrigger>
              <SelectValue placeholder="Select urgency" />
            </SelectTrigger>
            <SelectContent>
              {urgencyOptions.map((u) => (
                <SelectItem key={u} value={u}>{u}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="make">Vehicle Make / Model</Label>
          <Input id="make" placeholder="e.g. Ford Focus 2019" value={make} onChange={(e) => setMake(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reg">Registration (optional)</Label>
          <Input id="reg" placeholder="e.g. AB12 CDE" value={reg} onChange={(e) => setReg(e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Describe the Issue</Label>
        <Textarea id="message" rows={3} placeholder="Not sure? Just describe what's happening and we'll advise." value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="callback">Preferred Callback Time</Label>
        <Input id="callback" placeholder="e.g. mornings, after 2pm, anytime" value={callback} onChange={(e) => setCallback(e.target.value)} />
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-primary"
          checked={smsOptIn}
          onChange={(e) => setSmsOptIn(e.target.checked)}
        />
        <span className="text-sm text-foreground">
          <span className="font-medium">Text me a confirmation</span>
          <span className="block text-xs text-muted-foreground">
            One SMS to the number above with our address, entrance directions, and next steps. Reply STOP any time.
          </span>
        </span>
      </label>

      {error && <p className="text-sm text-destructive font-medium">{error}</p>}

      <Button type="submit" className="w-full" size="lg" disabled={submitting}>
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" /> Send Enquiry
          </>
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Need advice first?{" "}
        <a href={biz.phoneHref} onClick={() => trackEvent("call_click", { location: "quote_form" })} className="text-primary font-medium hover:text-primary/80">Call us now</a>
      </p>
    </form>
  );
}
